import React, { useEffect, useRef, useState } from 'react';
import './styles.scss';

type Phase = 'start' | 'complete' | 'cancelled';

interface EventDetail {
  phase: Phase;
  durationMs?: number;
}

// Cosmetic "fouille en cours" bar for dungeonSystem.ts's loot-container
// search delay (see dungeonSearchService.ts) - the server is the sole
// authority on when the container actually opens; this is purely a visual
// cue so the delay doesn't read as lag or a bug. The bar's fill is a CSS
// transition timed to the server-reported duration, not a real progress
// measurement.
const DungeonSearchProgress: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [durationMs, setDurationMs] = useState(5000);
  const [fillStarted, setFillStarted] = useState(false);
  const rafHandles = useRef<[number, number] | null>(null);

  useEffect(() => {
    const cancelPendingStart = () => {
      if (rafHandles.current) {
        cancelAnimationFrame(rafHandles.current[0]);
        cancelAnimationFrame(rafHandles.current[1]);
        rafHandles.current = null;
      }
    };

    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as EventDetail;
      cancelPendingStart();

      if (detail.phase === 'start') {
        setDurationMs(detail.durationMs ?? 5000);
        setFillStarted(false);
        setVisible(true);
        // Double rAF: a single frame can still land in the same paint cycle
        // as the initial 0% width (no transition would fire, it'd just snap
        // straight to 100%) - waiting a full extra frame guarantees the
        // browser actually painted 0% before the width changes.
        const first = requestAnimationFrame(() => {
          const second = requestAnimationFrame(() => setFillStarted(true));
          rafHandles.current = [first, second];
        });
        rafHandles.current = [first, first];
      } else {
        setVisible(false);
        setFillStarted(false);
      }
    };

    window.addEventListener('skymp5-client:dungeonSearch', handler);
    return () => {
      window.removeEventListener('skymp5-client:dungeonSearch', handler);
      cancelPendingStart();
    };
  }, []);

  if (!visible) return null;

  return (
    <div className="dungeon-search-overlay">
      <div className="dungeon-search-panel">
        <div className="dungeon-search-panel__row">
          <div className="dungeon-search-label">Fouille en cours...</div>
        </div>
        <div className="dungeon-search-bar-track">
          <div
            className="dungeon-search-bar-fill"
            style={{ transitionDuration: `${durationMs}ms`, width: fillStarted ? '100%' : '0%' }}
          />
        </div>
      </div>
    </div>
  );
};

export default DungeonSearchProgress;
